import { useEffect, useState } from 'react';
import { Copy, Eye } from '@phosphor-icons/react';
import { toast } from 'sonner';

const SITE = (process.env.REACT_APP_SITE_URL || 'https://goconvertdocuments.com').replace(/\/$/, '');

const POPULAR = [
  { id: 'pdf-to-word', label: 'PDF to Word' },
  { id: 'word-to-pdf', label: 'Word to PDF' },
  { id: 'merge-pdf', label: 'Merge PDF' },
  { id: 'compress-pdf', label: 'Compress PDF' },
  { id: 'pdf-to-jpg', label: 'PDF to JPG' },
  { id: 'jpg-to-pdf', label: 'JPG to PDF' },
  { id: 'ocr-pdf', label: 'OCR PDF' },
  { id: 'image-to-webp', label: 'Image to WEBP' },
];

export default function EmbedMarketing() {
  const [toolId, setToolId] = useState('pdf-to-word');

  useEffect(() => {
    document.title = 'Embed our free converter on your site | Go Convert Documents';
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', 'Free embeddable widget — drop a PDF, Word or image converter into your blog or SaaS in one line of HTML.');
  }, []);

  const snippet = `<iframe src="${SITE}/embed/${toolId}" width="100%" height="520" style="border:2px solid #0A0A0A" loading="lazy" title="Free ${toolId} converter"></iframe>`;

  return (
    <div className="max-w-4xl mx-auto px-6 py-12">
      <p className="text-xs font-mono-plex uppercase tracking-widest text-zinc-500">Embed widget</p>
      <h1 className="font-display font-bold text-4xl md:text-6xl leading-tight text-slate-950 mt-2">
        Add a free converter<br />
        <span className="bg-black text-white px-2">to your site</span>
      </h1>
      <p className="text-lg text-zinc-700 mt-6 leading-relaxed max-w-2xl">
        One-line HTML snippet. Works on any site — Ghost, WordPress, Webflow, Notion pages, plain HTML.
        No API key. Free forever. Just paste and publish.
      </p>

      <div className="mt-10 border-2 border-black bg-white p-5 brutal-shadow">
        <label className="font-mono-plex text-xs uppercase tracking-widest text-zinc-500 block">
          Pick a tool
        </label>
        <select
          data-testid="embed-tool-select"
          value={toolId}
          onChange={(e) => setToolId(e.target.value)}
          className="mt-2 w-full md:w-72 px-3 py-2 border-2 border-black bg-white font-mono-plex text-sm focus:outline-none"
        >
          {POPULAR.map((t) => (
            <option key={t.id} value={t.id}>{t.label}</option>
          ))}
        </select>
      </div>

      <h2 className="font-display font-bold text-2xl mt-10">1. Copy this HTML</h2>
      <div className="border-2 border-black bg-slate-950 text-slate-100 mt-3 relative">
        <button
          data-testid="embed-copy-btn"
          onClick={() => { navigator.clipboard.writeText(snippet); toast.success('Copied!'); }}
          className="absolute top-2 right-2 border-2 border-white/20 bg-black/60 hover:bg-black text-white text-xs font-mono-plex px-2 py-1 flex items-center gap-1"
          style={{ transition: 'background-color 0.15s ease' }}
        >
          <Copy weight="bold" size={12} /> copy
        </button>
        <pre className="p-4 pt-10 overflow-x-auto text-xs md:text-sm font-mono-plex whitespace-pre-wrap break-all">
          <code>{snippet}</code>
        </pre>
      </div>

      <h2 className="font-display font-bold text-2xl mt-12">2. Preview</h2>
      <p className="text-zinc-700 mt-2 leading-relaxed">
        This is exactly what your readers will see:
      </p>
      <div className="mt-4 border-4 border-dashed border-zinc-400 p-4 bg-white">
        <iframe
          key={toolId}
          data-testid="embed-preview-iframe"
          src={`/embed/${toolId}`}
          title={`${toolId} preview`}
          width="100%"
          height={520}
          style={{ border: '2px solid #0A0A0A' }}
          loading="lazy"
        />
      </div>

      <h2 className="font-display font-bold text-2xl mt-12">Why embed?</h2>
      <ul className="mt-3 space-y-2 text-zinc-800">
        <li className="flex items-start gap-2"><Eye weight="fill" className="text-emerald-600 mt-1 shrink-0" size={16} /> Give your visitors real value without leaving your page.</li>
        <li className="flex items-start gap-2"><Eye weight="fill" className="text-emerald-600 mt-1 shrink-0" size={16} /> Zero maintenance — we handle uploads, conversion and cleanup.</li>
        <li className="flex items-start gap-2"><Eye weight="fill" className="text-emerald-600 mt-1 shrink-0" size={16} /> Every embed carries a small &ldquo;Powered by&rdquo; link back to us — that&apos;s our only ask.</li>
      </ul>

      <div className="mt-12 border-2 border-black bg-yellow-100 p-6 brutal-shadow">
        <p className="font-mono-plex text-xs uppercase tracking-widest text-zinc-600">Attribution</p>
        <p className="font-display font-semibold text-xl mt-2">
          Please keep the small &ldquo;Powered by Go Convert Documents&rdquo; badge visible.
        </p>
        <p className="text-sm text-zinc-700 mt-2 leading-relaxed">
          If you need a white-label embed without the badge for a commercial site, ask us on the <a href="/contact" className="underline font-semibold">contact page</a>.
        </p>
      </div>
    </div>
  );
}
